/**
 * Care Report Submission Service
 * Manages care_reports — one submitted report per shift.
 *
 * Table schema (run in Supabase before using):
 *   CREATE TABLE care_reports (
 *       id                    UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
 *       shift_id              UUID NOT NULL REFERENCES shifts(id) ON DELETE CASCADE,
 *       client_name           TEXT NOT NULL,
 *       caretaker_email       TEXT NOT NULL,
 *       shift_start           TEXT NOT NULL,
 *       shift_end             TEXT NOT NULL,
 *       mileage               NUMERIC(8,2) DEFAULT 0,
 *       supplies_description  TEXT DEFAULT '',
 *       supplies_amount       NUMERIC(10,2) DEFAULT 0,
 *       important_information TEXT DEFAULT '',
 *       custom_answers        JSONB NOT NULL DEFAULT '{}'::jsonb,
 *       submitted_at          TIMESTAMPTZ NOT NULL DEFAULT NOW(),
 *       created_at            TIMESTAMPTZ NOT NULL DEFAULT NOW(),
 *       updated_at            TIMESTAMPTZ NOT NULL DEFAULT NOW()
 *   );
 *   CREATE UNIQUE INDEX idx_care_reports_shift_id ON care_reports(shift_id);
 *   CREATE TRIGGER trg_care_reports_updated_at
 *   BEFORE UPDATE ON care_reports
 *   FOR EACH ROW EXECUTE FUNCTION set_updated_at();
 */

import { supabase } from '../../utils/supabase_config';
import { SupabaseService } from './supabaseService';
import { TABLES } from './tables';

const TABLE = TABLES.CARE_REPORTS;

export class CareReportSubmissionService extends SupabaseService {
  /**
   * Submit (create or update) a care report for a shift.
   * Uses upsert on shift_id so re-submitting overwrites the existing report.
   * @param {Object} reportData
   * @param {string} reportData.shift_id
   * @param {string} reportData.client_name
   * @param {string} reportData.caretaker_email
   * @param {string} reportData.shift_start
   * @param {string} reportData.shift_end
   * @param {number} reportData.mileage
   * @param {string} reportData.supplies_description
   * @param {number} reportData.supplies_amount
   * @param {string} reportData.important_information
   * @param {Object} reportData.custom_answers  - { [questionId]: value }
   */
  static async submitReport(reportData) {
    try {
      const { error } = await supabase
        .from(TABLE)
        .upsert(reportData, { onConflict: 'shift_id' });
      if (error) throw error;
    } catch (error) {
      this.handleError('Error submitting care report', error);
      throw error;
    }
  }

  /**
   * Get the submitted report for a specific shift, or null if none exists.
   * @param {string} shiftId - UUID of the shift
   * @returns {Promise<Object|null>}
   */
  static async getReportForShift(shiftId) {
    try {
      const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .eq('shift_id', shiftId)
        .maybeSingle();
      if (error) throw error;
      return data ? this._addDocId(data) : null;
    } catch (error) {
      this.handleError('Error fetching care report for shift', error);
      throw error;
    }
  }

  /**
   * Get all submitted reports for a specific client, sorted by shift_start desc.
   * @param {string} clientName
   * @returns {Promise<Array>}
   */
  static async getReportsForClient(clientName) {
    try {
      const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .eq('client_name', clientName)
        .order('shift_start', { ascending: false });
      if (error) throw error;
      return data ?? [];
    } catch (error) {
      this.handleError('Error fetching care reports for client', error);
      throw error;
    }
  }

  /**
   * Update only the mileage on an existing report.
   * @param {string} reportId - UUID of the care_report row
   * @param {number} mileage
   */
  static async updateMileage(reportId, mileage) {
    try {
      const { error } = await supabase
        .from(TABLE)
        .update({ mileage: Number(mileage) || 0 })
        .eq('id', reportId);
      if (error) throw error;
    } catch (error) {
      this.handleError('Error updating mileage', error);
      throw error;
    }
  }

  /**
   * Get all submitted reports (admin use).
   * @returns {Promise<Array>}
   */
  static async getAllReports() {
    return super.getAll(TABLE, { orderBy: { field: 'submitted_at', direction: 'desc' } });
  }
}
